import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, Typography, Paper, Button, Grid, Chip, Divider, Menu, MenuItem, Avatar, Container, Stack
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SubjectIcon from '@mui/icons-material/Subject';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';

function ReferenceDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reference, setReference] = useState(null);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchDetails = async () => {
    try {
      const res = await axios.get(`${API_URL}/references/${id}`, { headers });
      setReference(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const handleStatusClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleStatusClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = async (newStatus) => {
    handleStatusClose();
    try {
      await axios.put(`${API_URL}/references/${id}`, { status: newStatus }, { headers });
      fetchDetails(); // Refresh
    } catch (err) {
      alert("Error updating status");
    }
  };

  if (loading) return <Loading message="Loading Reference Details..." />;
  if (!reference) return <Typography>Reference not found</Typography>;

  const getStatusColor = (status) => {
    switch(status) {
        case 'Approved': return 'success';
        case 'Pending': return 'warning';
        case 'Rejected': return 'error';
        default: return 'default';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Paper elevation={0} sx={{ 
        p: 4, 
        mb: 4, 
        borderRadius: 4, 
        background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)', 
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Decorative Circles */}
        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />
        <Box sx={{ position: 'absolute', bottom: -30, left: 100, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, alignItems: { xs: 'center', md: 'flex-start' }, justifyContent: 'space-between', flexWrap: 'wrap-reverse', gap: 2 }}>
                <Box sx={{ flex: 1, width: '100%' }}>
                    <Button 
                        startIcon={<ArrowBackIcon />} 
                        onClick={() => navigate('/references')} 
                        sx={{ color: 'rgba(255,255,255,0.8)', mb: 2, pl: 0, '&:hover': { color: 'white', bgcolor: 'transparent' } }}
                    >
                        Back to References
                    </Button>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 1, flexWrap: 'wrap' }}>
                        <Typography variant="h3" fontWeight="800" sx={{ letterSpacing: -1, fontSize: { xs: '2rem', md: '3rem' } }}>
                            {reference.ref_number}
                        </Typography>
                        <Chip 
                            label={reference.status} 
                            onClick={handleStatusClick}
                            onDelete={handleStatusClick}
                            deleteIcon={<EditIcon sx={{ color: `${getStatusColor(reference.status)}.main` }} />}
                            sx={{ 
                                bgcolor: 'white', 
                                color: 'primary.main', 
                                fontWeight: 'bold',
                                height: 28,
                                boxShadow: 2,
                                cursor: 'pointer'
                            }} 
                        />
                    </Box>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleStatusClose}
                    >
                        <MenuItem onClick={() => handleStatusChange('Pending')}>Pending</MenuItem>
                        <MenuItem onClick={() => handleStatusChange('Approved')}>Approved</MenuItem>
                        <MenuItem onClick={() => handleStatusChange('Rejected')}>Rejected</MenuItem>
                    </Menu>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', opacity: 0.9 }}>
                         <Chip label={reference.type} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                         
                         {reference.project_name && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <DescriptionIcon fontSize="small" />
                                <Typography variant="subtitle1">{reference.project_name}</Typography>
                            </Box>
                         )}
                    </Box>
                </Box>

                {/* QR Code Box */}
                <Box sx={{ 
                    bgcolor: 'white', 
                    p: 1.5, 
                    borderRadius: 2, 
                    boxShadow: 3,
                    textAlign: 'center',
                    alignSelf: { xs: 'center', md: 'flex-start' },
                    mb: { xs: 2, md: 0 }
                }}>
                    <img 
                        src={`${API_URL}/references/${id}/qr`} 
                        alt="QR Code" 
                        style={{ width: 100, height: 100, display: 'block' }} 
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', mt: 0.5, display: 'block', fontWeight: 'bold' }}>
                        SCAN ME
                    </Typography>
                </Box>
            </Box>
        </Box>
      </Paper>

      {/* Main Content Paper */}
      <Paper sx={{ mb: 3, p: 4, borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }} elevation={0}>
            <Grid container spacing={5}>
                {/* Main Info */}
                <Grid item xs={12} md={8}>
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="overline" color="text.secondary" fontWeight="bold" letterSpacing={1}>SUBJECT</Typography>
                        <Typography variant="h5" fontWeight="bold">
                            {reference.subject}
                        </Typography>
                    </Box>

                    <Divider sx={{ mb: 4 }} />

                    <Grid container spacing={4} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="overline" color="text.secondary" fontWeight="bold" letterSpacing={1}>RECIPIENT</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                <PersonIcon color="action" />
                                <Typography variant="body1" fontWeight="medium">
                                    {reference.recipient_to || '—'}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="overline" color="text.secondary" fontWeight="bold" letterSpacing={1}>ADDRESS</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                <LocationOnIcon color="action" />
                                <Typography variant="body1" fontWeight="medium">
                                    {reference.address || '—'}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>

                    <Box>
                        <Typography variant="overline" color="text.secondary" fontWeight="bold" letterSpacing={1}>DESCRIPTION</Typography>
                        <Paper variant="outlined" sx={{ p: 3, mt: 1, bgcolor: '#fafafa', borderRadius: 2 }}>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary', lineHeight: 1.7 }}>
                                {reference.description || 'No description provided.'}
                            </Typography>
                        </Paper>
                    </Box>
                </Grid>

                {/* Sidebar Info */}
                <Grid item xs={12} md={4}>
                    <Box sx={{ height: '100%', borderLeft: { md: '1px solid' }, borderColor: { md: 'divider' }, pl: { md: 4 } }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
                            Meta Information
                        </Typography>
                        
                        <Stack spacing={3}>
                            <Box>
                                <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" gutterBottom>DATE CREATED</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CalendarTodayIcon fontSize="small" color="action" />
                                    <Typography variant="body1">
                                        {reference.created_at}
                                    </Typography>
                                </Box>
                            </Box>

                            <Divider />

                            <Box>
                                <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" gutterBottom>CREATED BY</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontWeight: 'bold' }}>
                                        {reference.user_name ? reference.user_name.charAt(0).toUpperCase() : 'U'}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="body2" fontWeight="bold">
                                            {reference.user_name || 'Unknown User'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Creator
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Stack>
                    </Box>
                </Grid>
            </Grid>
      </Paper>
    </Container>
  );
}

export default ReferenceDetails;
